import { createActor, fromPromise } from "xstate"
import { isTruthy } from "remeda"
import { AuthMachine } from "@recordsure/unity-auth-machine"

import { config } from "../utils/config"

import type { ErrorObject, UserPermission } from "@recordsure/unity-auth-machine"

export let authMachine: ReturnType<typeof setupAuthMachine>

export function setupAuthMachine() {
  const actor = createActor(
    AuthMachine.provide({
      actors: {
        waitForRouter: fromPromise<void>(async () => {
          await Promise.resolve()
        }),
        routerRedirect: fromPromise<void, { url: string }>(async ({ input }) => {
          location.replace(input.url)
          await Promise.resolve()
        }),
        isAuthRoute: fromPromise<boolean, { stateKey: string }>(async ({ input }) => {
          await Promise.resolve()

          if (/^\/auth/i.test(location.pathname)) {
            const state = localStorage.getItem(input.stateKey)

            // We're calling back into auth but we've no state
            if (state == null) throw { code: "STA" } satisfies ErrorObject

            return true
          }

          return false
        }),
      },
    }),
    { input: { config } },
  ).start()

  authMachine = actor

  return actor
}

export function hasPermission(
  name: UserPermissionName,
  { organisation, project }: { organisation?: string | null; project?: string | null } = {},
) {
  const permissionState = authMachine.getSnapshot().context.permissions?.[name]
  if (permissionState == null) return false

  if (isTruthy(organisation)) {
    organisation = organisation.toLowerCase()

    // Get the organisation & project specific permission state
    if (isTruthy(project)) {
      project = project.toLowerCase()
      return permissionState[organisation]?.[project] ?? false
    }

    // Returns true if any of the projects in the specified organisation allows it
    const organisationPermissions = permissionState[organisation]
    return organisationPermissions != null && Object.values(organisationPermissions).some(Boolean)
  }

  // Returns true if any of the projects in any of the organisations allows it
  return Object.values(permissionState).flatMap(Object.values).some(Boolean)
}

export type UserPermissionName = "readAccess" | "writeAccess" | "deleteAccess" | (string & {})

export type ConfigUserPermission = UserPermission<UserPermissionName>
